import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { salvarOverrideIpca } from '~/Services/Service';
import AuthStorage from '~/Utils/AuthStorage';

const AVISO_IPCA = `Esta alteração afeta apenas fechamentos futuros.
                    Correções IPCA já lançadas não serão recalculadas.
                    Para corrigir saldos retroativamente,
                    lance ajuste manual em Fundos > Registrar Saque/Correção.`;

// DW-0982 P6 — aviso forte do recálculo retroativo em massa (botão "Alterar Todos os Fechamentos").
const montarAvisoRetroativo = competencia =>
  `Isto vai recalcular as correções de IPCA de TODOS os fechamentos a partir da competência ${competencia}, ` +
  'inclusive os já lançados. Os saldos dos fundos serão atualizados. Deseja continuar?';

const formatarCompetencia = value => {
  if (!value) return '-';
  const partes = String(value).split('-');
  if (partes.length < 2) return value;
  return `${partes[1]}/${partes[0]}`;
};

const percentualDigitadoParaDecimal = value => {
  const normalizado = String(value || '').replace(',', '.');
  return Number(normalizado) / 100;
};

const decimalParaPercentualDigitado = value => {
  if (value == null) return '';
  return String(Number(value) * 100).replace('.', ',');
};

const ModalSubstituirIPCA = ({ show, item, onHide, onSalvo }) => {
  const [percentual, setPercentual] = useState('');
  const [observacao, setObservacao] = useState('');
  const [erro, setErro] = useState('');
  const [confirmando, setConfirmando] = useState(false);
  const [processando, setProcessando] = useState(false);
  // DW-0982 P6: distingue "Salvar" (só futuros, ADR-008) de "Alterar Todos os Fechamentos"
  // (recálculo retroativo em massa, ADR-014). Define o aviso e a flag enviada ao backend.
  const [aplicarRetroativo, setAplicarRetroativo] = useState(false);

  useEffect(() => {
    if (!show) {
      setPercentual('');
      setObservacao('');
      setErro('');
      setConfirmando(false);
      setProcessando(false);
      setAplicarRetroativo(false);
      return;
    }
    if (item && item.tem_override) {
      setPercentual(decimalParaPercentualDigitado(item.valor_override));
      setObservacao(item.observacao_override || '');
    } else {
      setPercentual('');
      setObservacao('');
    }
    setErro('');
    setConfirmando(false);
    setAplicarRetroativo(false);
  }, [show, item]);

  const pedirSalvar = retroativo => {
    setErro('');
    const decimal = percentualDigitadoParaDecimal(percentual);
    if (Number.isNaN(decimal)) {
      setErro('Percentual inválido.');
      return;
    }
    if (decimal < 0) {
      setErro('Valor percentual não pode ser negativo.');
      return;
    }
    if (decimal > 0.5) {
      setErro('Valor percentual implausivelmente alto.');
      return;
    }
    if (observacao && observacao.length > 500) {
      setErro('Observação deve conter até 500 caracteres.');
      return;
    }
    setAplicarRetroativo(retroativo === true);
    setConfirmando(true);
  };

  const confirmar = async confirmou => {
    if (confirmou !== true) {
      setConfirmando(false);
      return;
    }

    const usuario = AuthStorage.getUser();
    const codigoUsuario = usuario && usuario.usuario ? Number(usuario.usuario) : 0;

    try {
      setProcessando(true);
      await salvarOverrideIpca({
        competencia: item.competencia,
        valor_percentual: percentualDigitadoParaDecimal(percentual),
        observacao,
        codigo_usuario: codigoUsuario,
        codigo_usuario_criador: codigoUsuario,
        aplicar_retroativo: aplicarRetroativo,
      });
      toast.success(
        aplicarRetroativo
          ? 'Substituição IPCA salva e fechamentos recalculados.'
          : 'Substituição IPCA salva.'
      );
      setConfirmando(false);
      if (onSalvo) await onSalvo();
      onHide();
    } catch (err) {
      const mensagem = err && err.message ? err.message : 'Erro ao salvar substituição.';
      toast.error(mensagem);
      setConfirmando(false);
    } finally {
      setProcessando(false);
    }
  };

  const titulo = item && item.tem_override
    ? 'Editar substituição IPCA'
    : 'Adicionar substituição IPCA';

  return (
    <>
      <Modal
        className={`hpj-modal hpj-modal-substituir-ipca${
          confirmando ? ' hpj-modal-stacked-back' : ''
        }`}
        show={show}
        onHide={onHide}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{titulo}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={5}>
              <Form.Group>
                <Form.Label>Competência</Form.Label>
                <Form.Control
                  type="text"
                  value={item ? formatarCompetencia(item.competencia) : ''}
                  readOnly
                  plaintext
                />
              </Form.Group>
            </Col>
            <Col md={7}>
              <Form.Group>
                <Form.Label>
                  IPCA (%)
                  <span className="hpj-required">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  inputMode="decimal"
                  placeholder="0,5"
                  value={percentual}
                  onChange={e => setPercentual(e.target.value)}
                  autoFocus
                />
                <div className="hpj-modal-helper">
                  Digite a porcentagem em decimal Ex: 0,5 = 50%.
                </div>
              </Form.Group>
            </Col>
          </Row>
          <Form.Group>
            <Form.Label>Observação</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={observacao}
              onChange={e => setObservacao(e.target.value)}
            />
          </Form.Group>
          {erro && <div className="hpj-field-error">{erro}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button className="hpj-btn" variant="outline-secondary" onClick={onHide}>
            Cancelar
          </Button>
          <Button className="hpj-btn hpj-btn-primary" onClick={() => pedirSalvar(false)}>
            Salvar
          </Button>
          <Button
            className="hpj-btn"
            variant="warning"
            onClick={() => pedirSalvar(true)}
          >
            Alterar Todos os Fechamentos
          </Button>
        </Modal.Footer>
      </Modal>

      <ModalConfirmacao
        show={confirmando}
        title={
          aplicarRetroativo
            ? 'Alterar TODOS os fechamentos'
            : 'Confirmar substituição IPCA'
        }
        message={
          aplicarRetroativo
            ? montarAvisoRetroativo(item ? formatarCompetencia(item.competencia) : '')
            : AVISO_IPCA
        }
        variant={aplicarRetroativo ? 'danger' : 'success'}
        onClose={confirmar}
        loading={processando}
      />
    </>
  );
};

export default ModalSubstituirIPCA;
